/**
 * Pure helpers for the Receive Purchase Order modal (Incoming Stock).
 *
 * Centralises the full / partial / not-received / over-received maths so the
 * behaviour is testable and consistent between the row rendering, the pre-submit
 * summary, and the split-PO payload. Keeping this logic out of the component
 * prevents subtle bugs like showing the ordered quantity where the *remaining*
 * (shortfall) quantity was meant, or silently sending negative / NaN quantities.
 *
 * Receipt line shape (from the modal state):
 *   { sku, quantity (ordered), received_quantity, is_received, title, ... }
 */

/** Coerce a received-quantity input into a safe non-negative integer. */
export function toReceivedQuantity(value) {
  const n = parseInt(value, 10);
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
}

/** Coerce an ordered quantity into a safe non-negative integer. */
export function toOrderedQuantity(value) {
  const n = parseInt(value, 10);
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
}

/**
 * Sanitise a raw text-input value for the received-qty field.
 * Keeps an empty string (so the operator can clear and retype) but never
 * allows negatives or non-numeric junk to reach the payload.
 * @returns {string} the sanitised string value to store in component state
 */
export function sanitizeReceivedQuantityInput(rawValue) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) return '';
  const n = parseInt(rawValue, 10);
  if (!Number.isFinite(n) || n < 0) return '0';
  return String(n);
}

/**
 * Classify a single receipt line.
 * @returns {'not_received'|'partial'|'full'|'over'}
 */
export function classifyReceiptLine(item) {
  const ordered = toOrderedQuantity(item?.quantity);
  const isReceived = item?.is_received === true;
  const received = isReceived ? toReceivedQuantity(item?.received_quantity) : 0;

  if (!isReceived || received <= 0) return 'not_received';
  if (received < ordered) return 'partial';
  if (received > ordered) return 'over';
  return 'full';
}

/**
 * Lines that still have an outstanding shortfall (ordered - received > 0).
 * Each returned line carries `splitQuantity` = the exact remaining amount that
 * should go onto the split (-PART2) PO. This is the number that must be shown
 * to the operator and sent to the split endpoint — NOT the full ordered qty.
 */
export function getUnreceivedSplitLines(items = []) {
  const lines = [];
  for (const item of items) {
    const ordered = toOrderedQuantity(item?.quantity);
    const isReceived = item?.is_received === true;
    const received = isReceived ? toReceivedQuantity(item?.received_quantity) : 0;
    const shortfall = Math.max(0, ordered - received);
    if (shortfall > 0) {
      lines.push({ ...item, splitQuantity: shortfall });
    }
  }
  return lines;
}

/** Hard cap on extra (not-on-PO) lines per receipt — guards against runaway payloads. */
export const MAX_EXTRA_ITEMS = 50;

/**
 * Clean the extra (not-on-PO) items list for submission.
 * An extra item is something that physically arrived in the shipment but has no
 * PO line: supplier mistakes, substitutions, samples. Shape:
 *   { sku, title, variantTitle?, quantity, source: 'catalog' | 'custom' }
 * Drops lines with no SKU AND no title, or zero/invalid quantity.
 */
export function sanitizeExtraItems(extraItems = []) {
  if (!Array.isArray(extraItems)) return [];
  const cleaned = [];
  for (const item of extraItems) {
    const sku = String(item?.sku ?? '').trim();
    const title = String(item?.title ?? '').trim();
    const quantity = toReceivedQuantity(item?.quantity);
    if (quantity <= 0) continue;
    if (!sku && !title) continue;
    cleaned.push({
      sku,
      title,
      variantTitle: String(item?.variantTitle ?? '').trim() || null,
      quantity,
      source: item?.source === 'custom' ? 'custom' : 'catalog'
    });
    if (cleaned.length >= MAX_EXTRA_ITEMS) break;
  }
  return cleaned;
}

/**
 * Extra items re-shaped as PO lines for the card list, worksheet and label print.
 * Mirrors `buildExtraReceiptOrderItems` in backend/services/purchaseOrderWorkflow.js —
 * keep the two in sync. Rows are flagged `isExtraItem` so the receive modal, split
 * maths and the weight check can keep ignoring them.
 */
export function buildExtraPoItems(extraItems = [], existingItems = []) {
  const takenSkus = new Set(
    (Array.isArray(existingItems) ? existingItems : [])
      .map(item => String(item?.sku || item?.variant_sku || '').trim().toUpperCase())
      .filter(Boolean)
  );

  const rows = [];
  for (const extra of sanitizeExtraItems(extraItems)) {
    const skuKey = extra.sku.toUpperCase();
    if (skuKey && takenSkus.has(skuKey)) continue;
    if (skuKey) takenSkus.add(skuKey);
    rows.push({
      sku: extra.sku,
      title: extra.title || extra.sku,
      variantTitle: extra.variantTitle,
      quantity: extra.quantity,
      isExtraItem: true,
      extraSource: extra.source
    });
  }
  return rows;
}

/** The PO's ordered lines — everything the extras merge appended is dropped. */
export function getOrderedPoLines(items = []) {
  return (Array.isArray(items) ? items : []).filter(item => item?.isExtraItem !== true);
}

/** Totals for the extra-items banner: line count + unit count (post-sanitise). */
export function summarizeExtraItems(extraItems = []) {
  const cleaned = sanitizeExtraItems(extraItems);
  return {
    count: cleaned.length,
    totalQty: cleaned.reduce((sum, line) => sum + line.quantity, 0)
  };
}

/**
 * Full summary of a receipt, used to drive the pre-confirmation banner and to
 * decide whether a split PO is required / an over-receive warning is shown.
 */
export function summarizeReceipt(items = []) {
  let full = 0;
  let partial = 0;
  let notReceived = 0;
  let over = 0;
  let overQty = 0;
  let receivedUnits = 0;
  let orderedUnits = 0;

  for (const item of items) {
    const ordered = toOrderedQuantity(item?.quantity);
    const isReceived = item?.is_received === true;
    const received = isReceived ? toReceivedQuantity(item?.received_quantity) : 0;
    orderedUnits += ordered;
    receivedUnits += received;

    const cls = classifyReceiptLine(item);
    if (cls === 'full') full += 1;
    else if (cls === 'partial') partial += 1;
    else if (cls === 'over') {
      over += 1;
      overQty += received - ordered;
    } else {
      notReceived += 1;
    }
  }

  const unreceivedLines = getUnreceivedSplitLines(items);
  const totalUnreceivedQty = unreceivedLines.reduce(
    (sum, line) => sum + (line.splitQuantity || 0),
    0
  );

  return {
    total: items.length,
    full,
    partial,
    notReceived,
    over,
    overQty,
    receivedUnits,
    orderedUnits,
    hasOverReceived: over > 0,
    unreceivedLines,
    unreceivedLineCount: unreceivedLines.length,
    totalUnreceivedQty,
    requiresSplit: unreceivedLines.length > 0,
    allFullyReceived: unreceivedLines.length === 0 && over === 0
  };
}
